import {
  nhanVienSearchColumns,
  nhomColumns,
  QUERY_KEYS,
  regionSearchColumns,
  supplierSearchColumns,
  vatTuSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import { DoiTuong, Group, KhoHang, KhuVuc, type NhanVien, VatTu } from '@/types/schemas';
import { SearchDialogActions, SearchDialogState } from '../../hooks';
import { FormField, SearchField } from '@/components/custom/erp';
import { useLoaiVatTu } from '@/hooks/queries/useLoaiVatTu';
import { Label } from '@/components/ui/label';

interface GeneralTabProps {
  searchState: {
    state: SearchDialogState;
    actions: SearchDialogActions;
  };
}

const GeneralTab: React.FC<GeneralTabProps> = ({ searchState: { state, actions } }) => {
  const { loaiVatTus } = useLoaiVatTu();

  const loaiVatTuOptions = loaiVatTus.map(item => ({
    value: item.uuid,
    label: `${item.ten_lvt}`
  }));

  return (
    <div className='h-96 w-[800px] min-w-[800px] space-y-1 p-4'>
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left'>Mã khách hàng</Label>
        <SearchField<DoiTuong>
          searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
          searchColumns={supplierSearchColumns}
          dialogTitle='Danh mục đối tượng'
          columnDisplay={`customer_code`}
          displayRelatedField={`customer_name`}
          value={state.khachHang?.customer_code || ''}
          relatedFieldValue={state.khachHang?.customer_name || ''}
          onRowSelection={actions.setKhachHang}
        />
      </div>

      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left'>Nhóm khách hàng</Label>
        <div className='flex gap-3'>
          <div className='flex-1'>
            <SearchField<Group>
              searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=KH1`}
              className='w-48'
              searchColumns={nhomColumns}
              dialogTitle='Danh mục nhóm khách hàng 1'
              columnDisplay={`ma_nhom`}
              value={state.nhomKhachHang1?.ma_nhom || ''}
              relatedFieldValue={state.nhomKhachHang1?.ten_phan_nhom || ''}
              onRowSelection={actions.setNhomKhachHang1}
            />
          </div>
          <div className='flex-1'>
            <SearchField<Group>
              searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=KH2`}
              searchColumns={nhomColumns}
              dialogTitle='Danh mục nhóm khách hàng 2'
              className='w-48'
              columnDisplay={`ma_nhom`}
              value={state.nhomKhachHang2?.ma_nhom || ''}
              relatedFieldValue={state.nhomKhachHang2?.ten_phan_nhom || ''}
              onRowSelection={actions.setNhomKhachHang2}
            />
          </div>
          <div className='flex-1'>
            <SearchField<Group>
              searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=KH3`}
              searchColumns={nhomColumns}
              dialogTitle='Danh mục nhóm khách hàng 3'
              className='w-48'
              columnDisplay={`ma_nhom`}
              value={state.nhomKhachHang3?.ma_nhom || ''}
              relatedFieldValue={state.nhomKhachHang3?.ten_phan_nhom || ''}
              onRowSelection={actions.setNhomKhachHang3}
            />
          </div>
        </div>
      </div>
      {/* Khu vực */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left'>Khu vực</Label>
        <SearchField<KhuVuc>
          searchEndpoint={`/${QUERY_KEYS.KHU_VUC}/`}
          searchColumns={regionSearchColumns}
          dialogTitle='Danh mục khu vực'
          columnDisplay={`rg_code`}
          displayRelatedField={`rgname`}
          value={state.khuVuc?.rg_code || ''}
          relatedFieldValue={state.khuVuc?.rgname || ''}
          onRowSelection={actions.setKhuVuc}
        />
      </div>
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left'>Mã nhân viên</Label>
        <SearchField<NhanVien>
          searchEndpoint={`/${QUERY_KEYS.NHAN_VIEN}/`}
          searchColumns={nhanVienSearchColumns}
          dialogTitle='Danh mục nhân viên'
          columnDisplay={`ma_nhan_vien`}
          displayRelatedField={`ho_ten_nhan_vien`}
          value={state.nhanVien?.ma_nhan_vien || ''}
          relatedFieldValue={state.nhanVien?.ho_ten_nhan_vien || ''}
          onRowSelection={actions.setNhanVien}
        />
      </div>

      {/* Mã vật tư */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left'>Mã vật tư</Label>
        <SearchField<VatTu>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTuSearchColumns}
          dialogTitle='Danh mục vật tư'
          columnDisplay={`ma_vt`}
          displayRelatedField={`ten_vt`}
          value={state.vatTu?.ma_vt || ''}
          relatedFieldValue={state.vatTu?.ten_vt || ''}
          onRowSelection={actions.setVatTu}
        />
      </div>

      {/* Loại vật tư */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left'>Loại vật tư</Label>
        <FormField name='ma_lvt' type='select' options={loaiVatTuOptions} className='w-48' />
        {/* Chỉ xem vật tư có theo dõi tồn kho */}
        <div className='ml-5 flex items-center'>
          <FormField name='ton_kho_yn' type='checkbox' className='flex-1' />
          <Label className='w-80 min-w-40 text-left'>Chỉ xem vật tư có theo dõi tồn kho</Label>
        </div>
      </div>

      {/* Nhóm vật tư - 3 field trong 1 hàng */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left'>Nhóm vật tư</Label>
        <div className='flex gap-3'>
          <SearchField<Group>
            searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=VT1`}
            searchColumns={nhomColumns}
            dialogTitle='Danh mục nhóm vật tư'
            className='w-48'
            columnDisplay={`ma_nhom`}
            value={state.nhomVatTu1?.ma_nhom || ''}
            relatedFieldValue={state.nhomVatTu1?.ten_phan_nhom || ''}
            onRowSelection={actions.setNhomVatTu1}
          />
          <SearchField<Group>
            searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=VT2`}
            searchColumns={nhomColumns}
            dialogTitle='Danh mục nhóm vật tư 2'
            className='w-48'
            columnDisplay={`ma_nhom`}
            value={state.nhomVatTu2?.ma_nhom || ''}
            relatedFieldValue={state.nhomVatTu2?.ten_phan_nhom || ''}
            onRowSelection={actions.setNhomVatTu2}
          />
          <SearchField<Group>
            searchEndpoint={`/${QUERY_KEYS.NHOM}/?loai_nhom=VT3`}
            searchColumns={nhomColumns}
            className='w-48'
            dialogTitle='Danh mục nhóm vật tư 3'
            columnDisplay={`ma_nhom`}
            value={state.nhomVatTu3?.ma_nhom || ''}
            relatedFieldValue={state.nhomVatTu3?.ten_phan_nhom || ''}
            onRowSelection={actions.setNhomVatTu3}
          />
        </div>
      </div>

      {/* Mã kho */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left'>Mã kho</Label>
        <SearchField<KhoHang>
          searchEndpoint={`/${QUERY_KEYS.KHO_HANG}/`}
          searchColumns={warehouseSearchColumns}
          dialogTitle='Danh mục kho hàng'
          columnDisplay={`ma_kho`}
          displayRelatedField={`ten_kho`}
          value={state.khoHang?.ma_kho || ''}
          relatedFieldValue={state.khoHang?.ten_kho || ''}
          onRowSelection={actions.setKhoHang}
        />
      </div>

      {/* Mẫu báo cáo */}
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left'>Mẫu báo cáo</Label>
        <div className='w-64'>
          <FormField
            name='mau_bc'
            type='select'
            options={[
              { value: 10, label: 'Mẫu số lượng' },
              { value: 20, label: 'Mẫu số lượng và giá trị' },
              { value: 30, label: 'Mẫu số lượng và giá trị ngoại tệ' }
            ]}
            className='w-full'
          />
        </div>
      </div>
      <div className='flex items-center'>
        <Label className='w-40 min-w-40 text-left'>Mẫu phân tích DL</Label>
        <div className='w-64'>
          <FormField
            name='data_analysis_struct'
            type='select'
            options={[
              { value: '0', label: 'Không phân tích' },
              { value: '1', label: 'Xoay theo số lượng, doanh thu' },
              { value: '2', label: 'Xoay theo số lượng' },
              { value: '3', label: 'Xoay theo doanh thu' },
              {
                value: '4',
                label: 'Xoay theo số lượng, doanh thu, vốn, lãi'
              }
            ]}
            className='w-full'
          />
        </div>
      </div>
    </div>
  );
};

export default GeneralTab;
